// components/CustomDatePicker.js
import React, { useState, useRef, useEffect } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from '../../../hooks/ThemeProvider';

const CustomDatePicker = ({ value, onChange, error }) => {
    const [isOpen, setIsOpen] = useState(false);
    const datepickerRef = useRef(null);
    const { theme } = useTheme();

    const [hour, setHour] = useState(12);
    const [minute, setMinute] = useState(0);
    const [meridiem, setMeridiem] = useState("AM");

    // ✅ Format date + time to show in input
    const formatFinalDateTime = () => {
        if (!value) return "";
        const datePart = value.toLocaleDateString();
        const hourFormatted = hour.toString().padStart(2, "0");
        const minuteFormatted = minute.toString().padStart(2, "0");
        return `${datePart} - ${hourFormatted}:${minuteFormatted} ${meridiem}`;
    };

    // ✅ Helper function to convert 12-hour to 24-hour format
    const convertTo24Hour = (h, m, meridiem) => {
        let hour24 = h;
        if (meridiem === "PM" && h !== 12) {
            hour24 = h + 12;
        } else if (meridiem === "AM" && h === 12) {
            hour24 = 0;
        }
        return { hour24, minute: m };
    };

    // ✅ Update parent state with new date and time combined
    const updateDateTimeInParent = (newDate, newHour, newMinute, newMeridiem) => {
        const { hour24 } = convertTo24Hour(newHour, newMinute, newMeridiem);
        const updatedDate = new Date(newDate);
        updatedDate.setHours(hour24, newMinute, 0, 0);
        onChange(updatedDate);
    };

    // ✅ When date is selected
    const handleDateChange = (date) => {
        updateDateTimeInParent(date, hour, minute, meridiem);
    };

    // ✅ Time Functions
    const increaseHour = () => {
        const newHour = hour === 12 ? 1 : hour + 1;
        setHour(newHour);
        updateDateTimeInParent(value, newHour, minute, meridiem);
    };

    const decreaseHour = () => {
        const newHour = hour === 1 ? 12 : hour - 1;
        setHour(newHour);
        updateDateTimeInParent(value, newHour, minute, meridiem);
    };

    const increaseMinute = () => {
        const newMinute = minute === 59 ? 0 : minute + 1;
        setMinute(newMinute);
        updateDateTimeInParent(value, hour, newMinute, meridiem);
    };

    const decreaseMinute = () => {
        const newMinute = minute === 0 ? 59 : minute - 1;
        setMinute(newMinute);
        updateDateTimeInParent(value, hour, newMinute, meridiem);
    };

    const toggleMeridiem = () => {
        const newMeridiem = meridiem === "AM" ? "PM" : "AM";
        setMeridiem(newMeridiem);
        updateDateTimeInParent(value, hour, minute, newMeridiem);
    };

    // ✅ Sync local time state when value prop changes (e.g., date selection)
    useEffect(() => {
        if (value) {
            const hours = value.getHours();
            const minutes = value.getMinutes();

            // Convert 24-hour to 12-hour format
            const meridiem = hours >= 12 ? "PM" : "AM";
            const hour12 = hours % 12 || 12;

            setHour(hour12);
            setMinute(minutes);
            setMeridiem(meridiem);
        }
    }, [value]);

    // ✅ Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datepickerRef.current && !datepickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="parentinner" ref={datepickerRef}>
            <div className="leftmain">
                <h3>Pool End Date & Time *</h3>
                <input
                    placeholder="MM/DD/YYYY"
                    type="text"
                    value={formatFinalDateTime()}
                    onFocus={() => setIsOpen(true)}
                    readOnly
                />
            </div>

            <div className="rightmainss">
                <span className={`calendarpsan ${isOpen ? "active" : ""}`} onClick={() => setIsOpen(!isOpen)}>
                    {theme === 'dark' ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M12 10.5V12.15L13.2 12.9" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 1.5V4.5" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15.75 5.625V4.5C15.75 4.10218 15.592 3.72064 15.3107 3.43934C15.0294 3.15804 14.6478 3 14.25 3H3.75C3.35218 3 2.97064 3.15804 2.68934 3.43934C2.40804 3.72064 2.25 4.10218 2.25 4.5V15C2.25 15.3978 2.40804 15.7794 2.68934 16.0607C2.97064 16.342 3.35218 16.5 3.75 16.5H6.375" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2.25 7.5H6" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 1.5V4.5" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 16.5C14.4853 16.5 16.5 14.4853 16.5 12C16.5 9.51472 14.4853 7.5 12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5Z" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M12 10.5V12.15L13.2 12.9" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 1.5V4.5" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15.75 5.625V4.5C15.75 4.10218 15.592 3.72064 15.3107 3.43934C15.0294 3.15804 14.6478 3 14.25 3H3.75C3.35218 3 2.97064 3.15804 2.68934 3.43934C2.40804 3.72064 2.25 4.10218 2.25 4.5V15C2.25 15.3978 2.40804 15.7794 2.68934 16.0607C2.97064 16.342 3.35218 16.5 3.75 16.5H6.375" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2.25 7.5H6" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 1.5V4.5" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 16.5C14.4853 16.5 16.5 14.4853 16.5 12C16.5 9.51472 14.4853 7.5 12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5Z" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </span>

                {isOpen && (
                    <div className="datepicker-dropdown">
                        <DatePicker
                            className='datttepicker'
                            selected={value}
                            onChange={handleDateChange}
                            dateFormat="MM/dd/yyyy"
                            minDate={new Date()}
                            inline
                        />

                        {/* TIME PICKER */}
                        <div className="endtime_sec">
                            <span>End Time</span>
                            <div className="timerssec">
                                <div className="hour">
                                    <p>{hour.toString().padStart(2, "0")}</p>
                                    <div className="svgstimes">
                                        {theme === 'dark' ? (
                                            <svg onClick={increaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08336L4.55577 3.13892L2.61133 5.08336" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={increaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08324L4.55577 3.13879L2.61133 5.08324" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                        {theme === 'dark' ? (
                                            <svg onClick={decreaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91664L4.55577 4.86108L2.61133 2.91664" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={decreaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91676L4.55577 4.86121L2.61133 2.91676" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                <h3>:</h3>

                                <div className="min">
                                    <p>{minute.toString().padStart(2, "0")}</p>
                                    <div className="svgstimes">
                                        {theme === 'dark' ? (
                                            <svg onClick={increaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08336L4.55577 3.13892L2.61133 5.08336" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={increaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08324L4.55577 3.13879L2.61133 5.08324" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                        {theme === 'dark' ? (
                                            <svg onClick={decreaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91664L4.55577 4.86108L2.61133 2.91664" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={decreaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91676L4.55577 4.86121L2.61133 2.91676" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                <div className="am-and-pm">
                                    <p>{meridiem}</p>
                                    <div className="svgstimes">
                                        {theme === 'dark' ? (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08336L4.55577 3.13892L2.61133 5.08336" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08324L4.55577 3.13879L2.61133 5.08324" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                        {theme === 'dark' ? (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91664L4.55577 4.86108L2.61133 2.91664" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91676L4.55577 4.86121L2.61133 2.91676" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomDatePicker;